import { Link } from "react-router-dom";
import { Bell, Bot, CalendarDays, Monitor } from "lucide-react";
import { SettingsSection } from "@/components/ui/SettingsSection";
import { SettingsRow } from "@/components/ui/SettingsRow";
import { Toggle } from "@/components/ui/Toggle";

/**
 * Preferences that nothing reads yet.
 *
 * gaia-app drew these as toggle-shaped divs; they are read-only indicators here for the same
 * reason - no handler means no control. Each becomes a real switch when something stores it.
 */
const PLACEHOLDER_PREFERENCES = {
  notifications: [
    { label: "Push notifications", description: "Receive alerts on your device", on: true },
    { label: "Email alerts", description: "Send critical alerts by email", on: false },
    { label: "Slack integration", description: "Post alerts to a Slack channel", on: true },
  ],
  display: [
    { label: "Language", value: "English" },
    { label: "Timezone", value: "Europe/Paris" },
    { label: "Units", value: "Metric" },
  ],
  gaia: [
    {
      label: "GAIA assistant",
      description: "Enable the AI assistant across all views",
      on: true,
    },
    { label: "Context sharing", description: "Allow GAIA to read current pod data", on: true },
  ],
} as const;

export function SettingsScreen() {
  return (
    <div className="mx-auto flex max-w-[640px] flex-col gap-8 p-8 max-md:p-4">
      <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>

      <SettingsSection title="Notifications" icon={<Bell className="h-3.5 w-3.5" aria-hidden />}>
        {PLACEHOLDER_PREFERENCES.notifications.map((item) => (
          <SettingsRow key={item.label} label={item.label} description={item.description}>
            <Toggle checked={item.on} label={item.label} />
          </SettingsRow>
        ))}
      </SettingsSection>

      <SettingsSection
        title="Connections"
        icon={<CalendarDays className="h-3.5 w-3.5" aria-hidden />}
      >
        {/* Connecting and disconnecting live on the calendar screen, where the consequence is
            visible - and where the code that owns that link already is. A feature does not reach
            into another feature; it links to it. */}
        <SettingsRow
          label="Google Calendar"
          description="Lets this app read and create BioPod events on your calendar"
        >
          <Link to="/calendar" className="text-sm font-medium text-accent hover:underline">
            Manage →
          </Link>
        </SettingsRow>
      </SettingsSection>

      <SettingsSection title="Display" icon={<Monitor className="h-3.5 w-3.5" aria-hidden />}>
        {PLACEHOLDER_PREFERENCES.display.map((item) => (
          <SettingsRow key={item.label} label={item.label}>
            <span className="shrink-0 text-sm text-text-muted">{item.value}</span>
          </SettingsRow>
        ))}
      </SettingsSection>

      <SettingsSection title="GAIA" icon={<Bot className="h-3.5 w-3.5" aria-hidden />}>
        {PLACEHOLDER_PREFERENCES.gaia.map((item) => (
          <SettingsRow key={item.label} label={item.label} description={item.description}>
            <Toggle checked={item.on} label={item.label} />
          </SettingsRow>
        ))}
      </SettingsSection>
    </div>
  );
}
